/**
 * 설치형 첫 실행 부트스트랩 마법사 (install-bootstrap Part B).
 *
 * 흐름: ①회사정보 입력 → ②최고관리자(ADMIN) 등록 → 완료 → 관리자 로그인 화면.
 *  - 회사정보 테이블이 비어있을 때만 App.tsx 가 본 화면을 렌더(setup/status needsSetup).
 *  - 제출은 BFF /api/setup/* → chat-server self-lock(설치 완료 후 409).
 *  - SETUP_TOKEN 이 서버에 설정된 경우 "설치 토큰" 입력 필요(미설정이면 비워도 됨).
 *  - 최고관리자는 즉시 로그인 가능. 단 첫 로그인 시 2단계 인증(TOTP) 등록이 요구된다(정책).
 */
import { useState, type ReactNode } from 'react';
import './SetupWizard.css';
import { Building2, FileText, User, Check, Save, MapPin, Phone, Mail, Globe, Briefcase, Lock } from '../../icons';
import {
  setupInstitution, setupAdmin, type SetupStep,
  type InstitutionSetupPayload,
} from '../../api/setup';

const digits = (s: string): string => s.replace(/\D/g, '');
function brnValid(raw: string): boolean {
  const d = digits(raw);
  if (d.length === 0) return false;
  if (!/^\d{10}$/.test(d)) return false;
  const n = d.split('').map(Number);
  const w = [1, 3, 7, 1, 3, 7, 1, 3, 5];
  let sum = 0;
  for (let i = 0; i < 9; i += 1) sum += n[i]! * w[i]!;
  sum += Math.floor((n[8]! * 5) / 10);
  return ((10 - (sum % 10)) % 10) === n[9]!;
}

function Field(props: {
  id: string; label: string; icon: ReactNode; value: string;
  onChange: (v: string) => void; max: number; placeholder?: string;
  required?: boolean; type?: string; err?: string | null; hint?: string;
}) {
  const { id, label, icon, value, onChange, max, placeholder, required, type, err, hint } = props;
  return (
    <div className="setup-field">
      <label className="setup-field__label" htmlFor={id}>
        {icon} {label}{required && <span className="setup-req">*</span>}
      </label>
      <input
        id={id} className="setup-input" type={type ?? 'text'} value={value} maxLength={max}
        onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        aria-invalid={err ? true : undefined}
      />
      {hint && !err && <p className="setup-field__hint">{hint}</p>}
      {err && <p className="setup-field__err">{err}</p>}
    </div>
  );
}

const EMPTY_INST = {
  name: '', businessRegNo: '', representativeName: '', businessType: '', businessItem: '',
  postalCode: '', address: '', addressDetail: '', phone: '', fax: '', companyEmail: '', domain: '',
};

export function SetupWizard({ initialStep, onDone }: { initialStep: SetupStep; onDone: () => void }) {
  const [step, setStep] = useState<SetupStep>(initialStep === 'done' ? 'institution' : initialStep);
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // ── 회사정보 ──
  const [inst, setInst] = useState({ ...EMPTY_INST });
  const setI = (k: keyof typeof EMPTY_INST, v: string) => setInst((s) => ({ ...s, [k]: v }));
  const [code, setCode] = useState<string | null>(null);

  // ── 최고관리자 ──
  const [admin, setAdmin] = useState({ email: '', name: '', password: '', confirm: '', phone: '' });
  const setA = (k: keyof typeof admin, v: string) => setAdmin((s) => ({ ...s, [k]: v }));

  const instNameInvalid = inst.name.trim().length === 0;
  const instBrnInvalid = !brnValid(inst.businessRegNo);
  const instEmailInvalid = inst.companyEmail.trim().length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inst.companyEmail.trim());
  const canSubmitInst = !instNameInvalid && !instBrnInvalid && !instEmailInvalid && !busy;

  const adminEmailInvalid = !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(admin.email.trim());
  const adminNameInvalid = admin.name.trim().length === 0;
  const pwInvalid = admin.password.length === 0;
  const confirmInvalid = admin.confirm !== admin.password;
  const canSubmitAdmin = !adminEmailInvalid && !adminNameInvalid && !pwInvalid && !confirmInvalid && !busy;

  function norm(v: string): string | null { const t = v.trim(); return t === '' ? null : t; }

  async function submitInstitution() {
    if (!canSubmitInst) return;
    setBusy(true); setError(null);
    try {
      const payload: InstitutionSetupPayload = {
        name: inst.name.trim(),
        businessRegNo: inst.businessRegNo.trim(),
        domain: norm(inst.domain),
        representativeName: norm(inst.representativeName),
        businessType: norm(inst.businessType),
        businessItem: norm(inst.businessItem),
        postalCode: norm(inst.postalCode),
        address: norm(inst.address),
        addressDetail: norm(inst.addressDetail),
        phone: norm(inst.phone),
        fax: norm(inst.fax),
        companyEmail: norm(inst.companyEmail),
      };
      const res = await setupInstitution(payload, token);
      setCode(res.code);
      setStep('superadmin');
    } catch (e: any) {
      setError(mapErr(e, '회사정보 저장에 실패했습니다.'));
    } finally {
      setBusy(false);
    }
  }

  async function submitAdmin() {
    if (!canSubmitAdmin) return;
    setBusy(true); setError(null);
    try {
      await setupAdmin({ email: admin.email.trim(), name: admin.name.trim(), password: admin.password, phone: norm(admin.phone) }, token);
      setStep('done');
    } catch (e: any) {
      setError(mapErr(e, '관리자 등록에 실패했습니다.'));
    } finally {
      setBusy(false);
    }
  }

  function mapErr(e: any, fallback: string): string {
    const c = e?.response?.data?.code;
    if (c === 'SETUP_TOKEN_INVALID') return '설치 토큰이 올바르지 않습니다.';
    if (c === 'INVALID_BUSINESS_REG_NO') return '사업자등록번호가 올바르지 않습니다(체크섬 불일치).';
    if (c === 'ALREADY_INITIALIZED') return '이미 회사정보가 등록되어 있습니다. 새로고침 후 로그인하세요.';
    if (c === 'ALREADY_HAS_ADMIN') return '이미 관리자가 등록되어 있습니다. 새로고침 후 로그인하세요.';
    if (c === 'NO_INSTITUTION') return '회사정보를 먼저 등록해 주세요.';
    if (c === 'EMAIL_EXISTS') return '이미 사용 중인 이메일입니다.';
    if (c === 'PASSWORD_POLICY') return e?.response?.data?.message ?? '비밀번호 정책을 만족하지 않습니다.';
    if (c === 'CONFLICT') return e?.response?.data?.message ?? '중복된 값이 있습니다. 다시 시도하세요.';
    return e?.response?.data?.message ?? fallback;
  }

  return (
    <div className="setup-screen">
      <div className="setup-card">
        <div className="setup-head">
          <span className="setup-logo"><Building2 size={22} /></span>
          <div>
            <h1 className="setup-title">최초 설치 구성</h1>
            <p className="setup-sub">서버를 사용하려면 회사정보와 최고관리자를 등록하세요.</p>
          </div>
        </div>

        <ol className="setup-steps">
          <li className={`setup-steps__item ${step === 'institution' ? 'is-active' : 'is-done'}`}>
            <span className="setup-steps__num">{step === 'institution' ? '1' : <Check size={13} />}</span> 회사정보
          </li>
          <li className={`setup-steps__item ${step === 'superadmin' ? 'is-active' : step === 'done' ? 'is-done' : ''}`}>
            <span className="setup-steps__num">{step === 'done' ? <Check size={13} /> : '2'}</span> 최고관리자
          </li>
          <li className={`setup-steps__item ${step === 'done' ? 'is-active' : ''}`}>
            <span className="setup-steps__num">3</span> 완료
          </li>
        </ol>

        {error && <div className="setup-alert" role="alert">{error}</div>}

        {/* ── STEP 1: 회사정보 ── */}
        {step === 'institution' && (
          <div className="setup-body">
            <div className="setup-group__title"><Building2 size={14} /> 기본정보</div>
            <Field id="s-name" label="회사명" icon={<Building2 size={14} />} value={inst.name} onChange={(v) => setI('name', v)} max={100} required placeholder="회사명" err={instNameInvalid ? '회사명을 입력하세요.' : null} />
            <Field id="s-brn" label="사업자등록번호" icon={<FileText size={14} />} value={inst.businessRegNo} onChange={(v) => setI('businessRegNo', v)} max={14} required placeholder="예: 123-45-67890" hint="하이픈 없이 입력해도 됩니다." err={inst.businessRegNo.length > 0 && instBrnInvalid ? '사업자등록번호가 올바르지 않습니다.' : null} />
            <Field id="s-domain" label="도메인" icon={<Globe size={14} />} value={inst.domain} onChange={(v) => setI('domain', v)} max={100} placeholder="예: example.com (선택)" hint="입력 시 이메일 도메인으로 로그인 식별에 사용됩니다." />

            <div className="setup-group__title"><FileText size={14} /> 사업자정보</div>
            <Field id="s-rep" label="대표자명" icon={<User size={14} />} value={inst.representativeName} onChange={(v) => setI('representativeName', v)} max={50} placeholder="대표자명 (선택)" />
            <Field id="s-bt" label="업태" icon={<Briefcase size={14} />} value={inst.businessType} onChange={(v) => setI('businessType', v)} max={100} placeholder="예: 서비스업 (선택)" />
            <Field id="s-bi" label="종목" icon={<Briefcase size={14} />} value={inst.businessItem} onChange={(v) => setI('businessItem', v)} max={100} placeholder="예: 소프트웨어 개발 (선택)" />

            <div className="setup-group__title"><MapPin size={14} /> 연락처·주소</div>
            <Field id="s-postal" label="우편번호" icon={<MapPin size={14} />} value={inst.postalCode} onChange={(v) => setI('postalCode', v)} max={10} placeholder="예: 06236 (선택)" />
            <Field id="s-addr" label="주소" icon={<MapPin size={14} />} value={inst.address} onChange={(v) => setI('address', v)} max={200} placeholder="기본주소 (선택)" />
            <Field id="s-addr2" label="상세주소" icon={<MapPin size={14} />} value={inst.addressDetail} onChange={(v) => setI('addressDetail', v)} max={200} placeholder="상세주소 (선택)" />
            <Field id="s-phone" label="전화번호" icon={<Phone size={14} />} value={inst.phone} onChange={(v) => setI('phone', v)} max={30} placeholder="예: 02-1234-5678 (선택)" />
            <Field id="s-fax" label="팩스" icon={<Phone size={14} />} value={inst.fax} onChange={(v) => setI('fax', v)} max={30} placeholder="예: 02-1234-5679 (선택)" />
            <Field id="s-email" label="회사 이메일" icon={<Mail size={14} />} value={inst.companyEmail} onChange={(v) => setI('companyEmail', v)} max={255} type="email" placeholder="예: contact@example.com (선택)" err={instEmailInvalid ? '이메일 형식이 올바르지 않습니다.' : null} />

            <div className="setup-group__title"><Lock size={14} /> 설치 토큰(선택)</div>
            <Field id="s-token" label="설치 토큰" icon={<Lock size={14} />} value={token} onChange={setToken} max={200} placeholder="서버에 SETUP_TOKEN 이 설정된 경우에만 입력" hint="미설정 환경이면 비워두세요." />

            <div className="setup-actions">
              <button type="button" className="setup-btn setup-btn--primary" disabled={!canSubmitInst} onClick={() => void submitInstitution()}>
                <Save size={14} /> {busy ? '저장 중…' : '다음: 최고관리자 등록'}
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 2: 최고관리자 ── */}
        {step === 'superadmin' && (
          <div className="setup-body">
            {code && <div className="setup-note"><Check size={14} /> 회사가 등록되었습니다. 회사코드: <code className="setup-code">{code}</code> (로그인 시 필요할 수 있으니 보관하세요)</div>}
            <div className="setup-group__title"><User size={14} /> 최고관리자(ADMIN)</div>
            <Field id="a-email" label="이메일(로그인 ID)" icon={<Mail size={14} />} value={admin.email} onChange={(v) => setA('email', v)} max={255} required type="email" placeholder="admin@example.com" err={admin.email.length > 0 && adminEmailInvalid ? '이메일 형식이 올바르지 않습니다.' : null} />
            <Field id="a-name" label="이름" icon={<User size={14} />} value={admin.name} onChange={(v) => setA('name', v)} max={50} required placeholder="관리자 이름" />
            <Field id="a-pw" label="비밀번호" icon={<Lock size={14} />} value={admin.password} onChange={(v) => setA('password', v)} max={200} required type="password" placeholder="비밀번호" />
            <Field id="a-pw2" label="비밀번호 확인" icon={<Lock size={14} />} value={admin.confirm} onChange={(v) => setA('confirm', v)} max={200} required type="password" placeholder="비밀번호 확인" err={admin.confirm.length > 0 && confirmInvalid ? '비밀번호가 일치하지 않습니다.' : null} />
            <Field id="a-phone" label="전화번호" icon={<Phone size={14} />} value={admin.phone} onChange={(v) => setA('phone', v)} max={20} placeholder="예: 010-1234-5678 (선택)" />
            <p className="setup-field__hint">등록 후 첫 로그인 시 보안을 위해 2단계 인증(TOTP) 등록이 요구됩니다.</p>

            <div className="setup-actions">
              <button type="button" className="setup-btn setup-btn--primary" disabled={!canSubmitAdmin} onClick={() => void submitAdmin()}>
                <Save size={14} /> {busy ? '등록 중…' : '관리자 등록 완료'}
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 3: 완료 ── */}
        {step === 'done' && (
          <div className="setup-body setup-done">
            <div className="setup-done__icon"><Check size={34} /></div>
            <h2 className="setup-done__title">설치가 완료되었습니다</h2>
            <p className="setup-done__desc">회사정보와 최고관리자 등록이 끝났습니다. 관리자 계정으로 로그인하세요.<br />첫 로그인 시 2단계 인증(TOTP) 등록 화면이 표시됩니다.</p>
            <button type="button" className="setup-btn setup-btn--primary" onClick={onDone}>로그인하러 가기</button>
          </div>
        )}
      </div>
    </div>
  );
}
